import type { AvailabilityAttachment, GhlAvailabilityStatus } from "../types/ghlBookingChat";

const KNOWN_STATUSES: GhlAvailabilityStatus[] = [
  "ok",
  "not_connected",
  "needs_reauth",
  "no_calendar",
  "calendar_not_found",
  "calendar_ambiguous",
  "unavailable",
  "invalid_date",
];

function isRecord(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === "object" && !Array.isArray(v);
}

/**
 * Parses `data.result` string from SSE tool_call for ghl_get_availability.
 * Defensive shape: never throws,
 * an unrecognized status degrades to "unavailable" rather than crashing the
 * card — the backend's own executor already treats every failure mode the
 * same way (never raises), so the frontend should too.
 */
export function parseAvailabilityToolResult(resultStr: string): AvailabilityAttachment | null {
  try {
    const raw = JSON.parse(resultStr) as Record<string, unknown>;
    if (!isRecord(raw)) return null;

    const status = typeof raw.status === "string" ? raw.status : "unavailable";
    const date = typeof raw.date === "string" ? raw.date : "";
    const durationMinutes = typeof raw.duration_minutes === "number" ? raw.duration_minutes : 30;

    if (status === "ok" && Array.isArray(raw.available_slots)) {
      const slots = raw.available_slots.filter((s): s is string => typeof s === "string");
      return {
        result: {
          status: "ok",
          date,
          duration_minutes: durationMinutes,
          available_slots: slots,
          has_availability: raw.has_availability === true || slots.length > 0,
          calendar_name: typeof raw.calendar_name === "string" ? raw.calendar_name : undefined,
          timezone: typeof raw.timezone === "string" ? raw.timezone : undefined,
        },
      };
    }

    const safeStatus = KNOWN_STATUSES.includes(status as GhlAvailabilityStatus) && status !== "ok"
      ? (status as Exclude<GhlAvailabilityStatus, "ok">)
      : "unavailable";

    return {
      result: {
        status: safeStatus,
        date,
        duration_minutes: durationMinutes,
        message: typeof raw.message === "string" ? raw.message : undefined,
        matching_calendars: Array.isArray(raw.matching_calendars)
          ? raw.matching_calendars.filter((c): c is string => typeof c === "string")
          : undefined,
      },
    };
  } catch {
    return null;
  }
}
